/* @flow */
import React, { memo, useState } from 'react';
import { Modal, Spin, Typography } from 'antd';
import useWindowSize from '@rehooks/window-size';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import styles from './TermsAndConditions.module.pcss';

type Props = {
  isPrivacyPolicyModalOpen: boolean,
  isTermsOfServiceModalOpen: boolean,
  isPlatformAgreementsModalOpen: booloean,
  togglePrivacyPolicyModal: (isModalOpen: boolean) => void,
  toggleTermsOfServiceModal: (isModalOpen: boolean) => void,
  togglePlatformAgreementsModal: (isModalOpen: boolean) => void,
  termsPolicyPayload: {
    is_terms_accepted: boolean,
    privacy_policy_file: string,
    terms_of_service_file: string,
    platform_agreement_file: String,
  },
  acceptTermsAndPolicy: ({ is_accepted: boolean }) => Promise<any>,
  acceptTermsAndPolicyLoading: boolean,
};

const TermsAndConditions = ({
  isPrivacyPolicyModalOpen,
  isTermsOfServiceModalOpen,
  isPlatformAgreementsModalOpen,
  togglePrivacyPolicyModal,
  toggleTermsOfServiceModal,
  togglePlatformAgreementsModal,
  termsPolicyPayload,
  acceptTermsAndPolicy,
  acceptTermsAndPolicyLoading,
}: Props) => {
  const [policyNumPages, setPolicyNumPages] = useState(0);
  const [termsNumPages, setTermsNumPages] = useState(0);
  const [platformNumPages, setPlatformNumPages] = useState(0);

  const onAcceptTermsAndPolicy = () => {
    acceptTermsAndPolicy({ is_accepted: true });
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    if (isPrivacyPolicyModalOpen) {
      setPolicyNumPages(numPages);
    }
    if (isTermsOfServiceModalOpen) {
      setTermsNumPages(numPages);
    }
    if (isPlatformAgreementsModalOpen) {
      setPlatformNumPages(numPages);
    }
  };

  const windowWidth = useWindowSize().innerWidth;
  let pdfPageWidth = windowWidth;
  if (windowWidth > 1440) {
    pdfPageWidth = windowWidth - 900;
  } else if (windowWidth <= 1440) {
    pdfPageWidth = windowWidth - 600;
  }

  const policyPagesCount = Array.from(new Array(policyNumPages), (el, i) => i + 1);
  const termsPagesCount = Array.from(new Array(termsNumPages), (el, i) => i + 1);
  const platformPagesCount = Array.from(new Array(platformNumPages), (el, i) => i + 1);

  const renderPrivacyPolicyModal = () => {
    return (
      <Modal
        destroyOnClose
        title={<Typography.Text strong>Privacy Policy</Typography.Text>}
        visible={isPrivacyPolicyModalOpen}
        onOk={() => {
          togglePrivacyPolicyModal(false);
          toggleTermsOfServiceModal(true);
        }}
        onCancel={() => togglePrivacyPolicyModal(false)}
        style={{ top: 10 }}
        bodyStyle={{ height: 'calc(80vh - 30px)', overflow: 'auto', padding: 0 }}
        width={'calc(70vw - 30px)'}
        okText="Accept"
        cancelText="Decline"
        className={styles.modal}
      >
        <Document
          className={styles.document}
          loading={<Spin />}
          file={termsPolicyPayload.privacy_policy_file}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {policyPagesCount.map((page) => (
            <div key={page} className={styles.pageWrapper}>
              <Page
                className={styles.page}
                loading={<Spin size="small" />}
                key={`page_${page}`}
                pageNumber={page}
                width={pdfPageWidth}
              />
            </div>
          ))}
        </Document>
      </Modal>
    );
  };

  const renderTermsOfServiceModal = () => {
    return (
      <Modal
        destroyOnClose
        title={<Typography.Text strong>Terms Of Service</Typography.Text>}
        visible={isTermsOfServiceModalOpen}
        onOk={() => {
          toggleTermsOfServiceModal(false);
          togglePlatformAgreementsModal(true);
        }}
        onCancel={() => toggleTermsOfServiceModal(false)}
        style={{ top: 10 }}
        bodyStyle={{ height: 'calc(80vh - 30px)', overflow: 'auto', padding: 0 }}
        width={'calc(70vw - 30px)'}
        okText="Accept"
        cancelText="Decline"
        className={styles.modal}
      >
        <Document
          className={styles.document}
          loading={<Spin />}
          file={termsPolicyPayload.terms_of_service_file}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {termsPagesCount.map((page) => (
            <div key={page} className={styles.pageWrapper}>
              <Page
                className={styles.page}
                loading={<Spin size="small" />}
                key={`page_${page}`}
                pageNumber={page}
                width={pdfPageWidth}
              />
            </div>
          ))}
        </Document>
      </Modal>
    );
  };
  const renderPlatformAgreementsModal = () => {
    return (
      <Modal
        destroyOnClose
        title={<Typography.Text strong>Platform Access Agreement</Typography.Text>}
        visible={isPlatformAgreementsModalOpen}
        onOk={onAcceptTermsAndPolicy}
        onCancel={() => togglePlatformAgreementsModal(false)}
        style={{ top: 10 }}
        bodyStyle={{ height: 'calc(80vh - 30px)', overflow: 'auto', padding: 0 }}
        width={'calc(70vw - 30px)'}
        okText="Accept"
        cancelText="Decline"
        className={styles.modal}
        confirmLoading={acceptTermsAndPolicyLoading}
      >
        <Document
          className={styles.document}
          loading={<Spin />}
          file={termsPolicyPayload.platform_agreement_file}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {platformPagesCount.map((page) => (
            <div key={page} className={styles.pageWrapper}>
              <Page
                className={styles.page}
                loading={<Spin size="small" />}
                key={`page_${page}`}
                pageNumber={page}
                width={pdfPageWidth}
              />
            </div>
          ))}
        </Document>
      </Modal>
    );
  };

  return (
    <div>
      {renderPrivacyPolicyModal()}
      {renderTermsOfServiceModal()}
      {renderPlatformAgreementsModal()}
    </div>
  );
};

const TermsAndConditionsMemo = memo<Props>(TermsAndConditions);

export default TermsAndConditionsMemo;
